import { analytics } from './analytics.js';
import { apiScreens, panelStateEventName } from './constants.js';
import { closeIconSvg } from './leadwin-agent.js';
import { effectiveLanguage, runtime } from './runtime.js';
import { launcherColors, panelChrome } from './color.js';
import { isStage, origin } from './snippet.js';
import {
    PANEL_COLLAPSE_MS,
    PANEL_EXPAND_MS,
    applyPanelGeometry,
    isPanelFullscreen,
    paintLauncherIcon,
    paintLauncherPulse,
    paintPanelGround,
    panelSizeTransition,
} from './chrome.js';
import { mobileViewportQuery } from './page.js';

/**
 * Everything the panel does on command: opening and closing it, the screen the chat opens on, the
 * look it wears, and the language it speaks. Fifth and last of init()'s surfaces.
 *
 * The hub of the instance — eleven of its thirteen functions are called from somewhere else, and it
 * reaches into three of the other four surfaces itself. That is not an accident of the cut: every
 * command the host page can give lands here, and every surface that wants the chat opened has to go
 * through the one function that keeps the launcher icon, the aria state and the frame in step.
 * Constructed last, so it can take the other surfaces by value; they reach back into it through the
 * late-bound arrows in init.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} The commands the rest of the instance and the host page's API dispatch to
 */
/**
 * How the panel arrives and leaves: a soft rise out of the launcher's corner, opacity and transform
 * only. Kept apart from panelSizeTransition() (chrome.js), which moves the box between its two sizes
 * while it stays open. Reassigning `transition` per call is only safe because the two never run at
 * once, and that is setExpanded's guard doing the work — see the note there.
 */
const PANEL_OPEN_TRANSITION =
    'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 300ms cubic-bezier(0.16, 1, 0.3, 1)';
const PANEL_CLOSED_TRANSFORM = 'translateY(16px) scale(0.97)';

/**
 * The same arrival for a panel that takes the whole screen. The corner one shrinks into the launcher it
 * came out of, which is the right story there and the wrong one here: scaling a fullscreen box leaves a
 * frame of the host page showing around all four edges for the length of the animation, and on a phone
 * that reads as the chat failing to fit rather than as it arriving. A screen that rises and settles is
 * what a phone does with a sheet. The distance stays small on purpose — a full slide from below the fold
 * would have the fixed box grow the host page's scroll area while it travels.
 */
const PANEL_CLOSED_TRANSFORM_FULLSCREEN = 'translateY(24px)';

/** How long the close is given before the panel really leaves the page — the transform's duration. */
const PANEL_CLOSE_HIDE_MS = 320;

export function createPanelControls(ctx) {
    const { state, options, panel, button, trust, frame, proactive } = ctx;

    /* The panel's LOGICAL state, which every "is the chat open?" question reads. It cannot be the
     * display style anymore: a closing panel stays displayed for the length of its exit animation,
     * and a check that reads the style during those 300ms would call a closed chat open. */
    state.panelOpen = false;

    let hideTimer = null;

    /* Whether the page under the panel is currently stepped back for a fullscreen chat (see
     * applyFullscreenTakeover), and what its own overflow said before we did. Null while nothing is
     * held — a restore that ran twice would write 'hidden' back onto a page that had none. */
    let fullscreenTakeoverActive = false;
    let scrollLockBefore = null;

    /**
     * Drops an article still waiting for the circuit's subscription, and the help link waiting on it.
     * Called wherever the visitor has moved on before the article could arrive.
     */
    function forgetPendingArticle() {
        state.pendingArticleId = null;
        state.pendingArticleRequestId = null;
        ctx.forgetArticleLink?.();
    }

    /** Opens/closes the panel and keeps the launcher icon + aria state in sync. */
    function setOpen(open) {
        if (open && !state.iframe) {
            // Lazy: the iframe (and with it the Blazor circuit later) loads on first open only.
            frame.mountFrame();
        }

        // Read before the assignment below, so the host page hears about a change and not about every
        // call: openArticle and openScreen both open an already-open panel, and a listener that dims
        // the page on each of them would dim it twice and undim it once.
        const wasOpen = state.panelOpen;

        state.panelOpen = open;

        if (!open) {
            // A help link's article still waiting for its circuit dies with the panel: replayed later it
            // would open, or fail and navigate, behind a panel the visitor has closed.
            forgetPendingArticle();
        }

        if (hideTimer !== null) {
            clearTimeout(hideTimer); // A reopen mid-close keeps the panel; the animation turns around.
            hideTimer = null;
        }

        if (open) {
            const wasHidden = panel.style.display === 'none';
            // Before the first paint of this open: a visitor who rotated the phone, or resized the window,
            // while the panel was hidden gets the box the screen they have now asks for.
            applyPanelGeometry(panel, options, state.panelExpanded === true);
            applyFullscreenTakeover(true);
            panel.style.display = 'block';

            if (canAnimatePanel()) {
                panel.style.transformOrigin = 'bottom ' + (options.side === 'left' ? 'left' : 'right');

                if (wasHidden) {
                    // Commit the start frame in its own style pass, or the browser only ever sees the
                    // end state and the first open pops instead of rising.
                    panel.style.transition = 'none';
                    panel.style.opacity = '0';
                    panel.style.transform = closedTransform();
                    panel.getBoundingClientRect();
                }

                panel.style.transition = PANEL_OPEN_TRANSITION;
            }

            panel.style.opacity = '1';
            panel.style.transform = 'none';
        } else if (canAnimatePanel() && panel.style.display !== 'none') {
            applyFullscreenTakeover(false);
            panel.style.transformOrigin = 'bottom ' + (options.side === 'left' ? 'left' : 'right');
            panel.style.transition = PANEL_OPEN_TRANSITION;
            panel.style.opacity = '0';
            panel.style.transform = closedTransform();

            hideTimer = setTimeout(function () {
                hideTimer = null;
                panel.style.display = 'none';
                // Back to the corner size while it is hidden, so the next open starts where a chat
                // belongs rather than in the box the last article was read in. Instantly: an animation
                // behind a hidden panel is only a chance for a reopen to catch it mid-shrink.
                setExpanded(false, true);
            }, PANEL_CLOSE_HIDE_MS);
        } else {
            // No animation to run (reduced motion, no matchMedia, or already hidden): leave at once.
            applyFullscreenTakeover(false);
            panel.style.display = 'none';
            setExpanded(false, true);
        }

        button.setAttribute('aria-expanded', String(open));
        if (open) {
            button.innerHTML = closeIconSvg;
        } else {
            paintLauncherIcon(button, options);
        }
        if (state.trustCardsCorner) {
            // The open panel has the visitor's attention (and the profile one tap away inside it); a
            // card beside the collapse control would only compete with it, and the timer goes with it:
            // rotating a card nobody can see would only cost the visitor's device work while they are
            // typing. The whole corner goes, not just the board — its close control and the opt-out
            // chip that may stand there instead belong to the same surface.
            state.trustCardsCorner.style.display = open ? 'none' : '';
            if (open) {
                trust.stopTrustCardRotation();
            } else {
                trust.startTrustCardRotation();
            }
        }
        frame.postPanelState(open);

        if (open !== wasOpen) {
            announcePanelState(open);
        }

        if (open) {
            // Nothing left to tease: the visitor is in the chat, where the starters live anyway. An
            // open modal goes with the bubbles — the chat now has the attention it was asking for.
            proactive.retireNudges('chat opened');
            proactive.closeAnnouncementModal();
            analytics.onWidgetOpened();
        } else {
            // The mirror of that line: a visitor who asked for the bubbles back is owed a round, and the
            // only place they could ask is the chat that was standing here. Does nothing on an ordinary
            // close, which is every close but that one.
            proactive.scheduleNudgesAfterClose();
        }

        // The unread dot and card stand only on a closed launcher: the open panel is where the visitor
        // reads, and closing it without reading brings both back.
        ctx.inbox?.refresh();
    }

    /**
     * Tells the host page that the panel opened or closed, and nothing else. The only thing this loader
     * says outwards.
     *
     * A plain CustomEvent on the document rather than a subscribe API: there is nothing to register and
     * nothing to unregister, no handler whose lifetime we would have to own, and two lines to listen
     * with. It bubbles, so a listener on window catches it too.
     *
     * What the page then does is the page's own business. The loader deliberately does not dim, move or
     * recolour anything of the host's — Feedbax' own app dims the drawers this panel covers, and the
     * next provider who embeds the widget may want something else entirely or nothing at all. A loader
     * that decided that for them would be one they could not use.
     * @param {boolean} open - Whether the panel is now open
     */
    function announcePanelState(open) {
        try {
            document.dispatchEvent(new CustomEvent(panelStateEventName, {
                bubbles: true,
                detail: { open: open }
            }));
        } catch { /* an engine without CustomEvent: a missing notification must not break the panel */ }
    }

    /**
     * Grows the panel to reading size, or puts it back. Asked for from inside the frame, where the visitor
     * opens a help-center article: the panel's box belongs to this page, and the iframe cannot resize the
     * element it sits in.
     *
     * The transition is (re)assigned on every call rather than set once at creation, because the preference
     * behind it is about this moment — a visitor who turns reduced motion on mid-session means it now. Both
     * the property and the size land in the same task, so the browser still animates the first change.
     * Growing and shrinking are given different times (chrome.js): the same curve read in both directions
     * would make the way back feel slower than the way out, because the visitor has nothing left to look at.
     * @param {boolean} expanded - True for the reading size, false for the corner panel
     * @param {boolean} [instant] - Skip the animation; for the reset behind a panel nobody can see
     */
    function setExpanded(expanded, instant) {
        const next = expanded === true;

        // Not while the panel is leaving. Closing from inside an open article sends both messages on the
        // one click — the close at once, the collapse a circuit round trip later — and this one would land
        // in the middle of the exit animation: `transition` is the close's for those 320ms, and rewriting
        // it here (with the size curve or with 'none') drops opacity and transform where they stand, so
        // the panel vanishes instead of falling back into the launcher. Nothing is lost by skipping it,
        // because setOpen's own reset puts the box back to corner size behind the hidden panel — and that
        // reset is the one caller that passes `instant`, which is what lets it through this guard.
        if (!state.panelOpen && instant !== true) {
            return;
        }

        if (state.panelExpanded === next) {
            return;
        }

        state.panelExpanded = next;

        // On a phone the state is bookkeeping and nothing else: the panel is already the whole screen, so
        // the reading size has nothing left to claim. Recorded anyway — a visitor who reads an article and
        // then rotates into a roomy window is owed the wide box there, and that switch reads this flag.
        if (isPanelFullscreen()) {
            return;
        }

        panel.style.transition = instant === true || prefersReducedMotion()
            ? 'none'
            : panelSizeTransition(next ? PANEL_EXPAND_MS : PANEL_COLLAPSE_MS);
        applyPanelGeometry(panel, options, next);
    }

    /** How this panel leaves and arrives — a corner box falls back into its launcher, a screen settles. */
    function closedTransform() {
        return isPanelFullscreen() ? PANEL_CLOSED_TRANSFORM_FULLSCREEN : PANEL_CLOSED_TRANSFORM;
    }

    /**
     * The two things a panel that owns the whole screen has to do to the page underneath it, and the
     * only two: put the launcher away, and stop the page behind from scrolling.
     *
     * This file otherwise makes a point of leaving the host page alone (see announcePanelState). The
     * exception is narrow and it is the visitor's: the launcher sits at a HIGHER z-index than the panel,
     * so on a fullscreen chat it floats on top of the composer the visitor is typing in; and a page that
     * keeps scrolling under a chat covering all of it means every flick meant for the transcript moves
     * somebody else's website instead — on iOS it also drags the whole chat with it. Both are undone on
     * close, down to the inline values the page had before, and neither happens on a corner panel.
     * @param {boolean} open - Whether the panel is (about to be) on screen
     */
    function applyFullscreenTakeover(open) {
        const takeover = open && isPanelFullscreen();

        if (takeover === fullscreenTakeoverActive) {
            return;
        }

        fullscreenTakeoverActive = takeover;
        // Back to 'flex', not to the empty string: the launcher centres its mark with the display of its
        // own cssText, and clearing the property would hand the sparkles back to the browser's default
        // for a button — the icon then sits off-centre for the rest of the visit.
        button.style.display = takeover ? 'none' : 'flex';

        try {
            const root = document.documentElement;

            if (takeover) {
                scrollLockBefore = { root: root.style.overflow, body: document.body.style.overflow };
                root.style.overflow = 'hidden';
                document.body.style.overflow = 'hidden';
            } else if (scrollLockBefore) {
                root.style.overflow = scrollLockBefore.root;
                document.body.style.overflow = scrollLockBefore.body;
                scrollLockBefore = null;
            }
        } catch { /* a page that replaced its own body — the panel is still open and still usable */ }
    }

    /** Whether this visitor asked their system for less movement. Never throws — matchMedia can be absent. */
    function prefersReducedMotion() {
        try {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch {
            return false;
        }
    }

    /**
     * Whether the open/close may animate at all: a real matchMedia (an environment without one cannot
     * answer the reduced-motion question — jsdom in the tests, which read the display style in the same
     * tick) and no reduced-motion preference. Falls to the instant path on both, never throws.
     */
    function canAnimatePanel() {
        try {
            return typeof window.matchMedia === 'function'
                && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch {
            return false;
        }
    }

    /* ------------------------------------------------------------------------------------------------
     * Public JS API (see the header comment). Everything here is defensive: it runs on someone else's
     * website, fed by someone else's code — unknown input is dropped, and nothing may ever throw.
     * ---------------------------------------------------------------------------------------------- */

    /**
     * The validated screen name of an open command's argument, or null for "just the chat". Accepts the
     * documented object form ({screen: 'lead'}) and a bare string, because both are obvious ways to
     * call it and rejecting one of them helps nobody.
     * @param {unknown} arg - Second argument of LeadWin('open', …)
     * @returns {string|null}
     */
    function readScreen(arg) {
        const raw = typeof arg === 'string'
            ? arg
            : arg && typeof arg.screen === 'string' ? arg.screen : '';
        const screen = raw.trim().toLowerCase();

        return apiScreens.includes(screen) ? screen : null;
    }

    function postScreenToFrame(screen) {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage({ type: 'feedbax:widget:showpanel', screen: screen }, origin);
        } catch { /* frame not loaded yet — the pending route below covers it */ }
    }

    /**
     * Routes a screen request to whoever can take it: a live circuit gets the message directly, anything
     * earlier parks the request — the next mount carries it in the frame URL, and the circuit's
     * subscription handshake replays it if the frame is already loading.
     * @param {string} screen - Validated screen name
     */
    function requestScreen(screen) {
        if (state.iframe && state.frameSubscribed) {
            postScreenToFrame(screen);
            return;
        }

        state.pendingScreen = screen;
        if (state.iframe) {
            postScreenToFrame(screen); // Best effort into the startup gap; the handshake replays it.
        }
    }

    /**
     * LeadWin('open', …): expands the panel, on the requested screen where one was named. The
     * screen is requested BEFORE the open so a first open's mount already builds the deep-linked URL.
     * @param {string|null} screen - Validated screen name, or null for the chat itself
     */
    function openScreen(screen) {
        if (screen) {
            forgetPendingArticle(); // The later request wins; replayed after it, the article would not.
            requestScreen(screen);
        }

        if (!state.panelOpen) {
            setOpen(true);
        }
    }

    /**
     * Longest article key this accepts. It is WidgetKbSlug.MaxLength, because a slug IS one of the three
     * key forms the article reader takes (GUID, slug, Intercom id) and in practice the only one a provider
     * can get hold of: the GUID appears nowhere in the manage UI, so what they copy is the last segment of
     * the article's public address. The cap was 64 and that silently broke the call for every article
     * whose title is long enough — the slug base alone runs to 120 characters, plus a collision suffix.
     * A bound is still worth having (the host page can post anything), it just has to be the product's own.
     */
    const maxArticleKeyLength = 300;

    /**
     * The validated article id of an article command's argument, or null. Accepts the same two shapes the
     * screen argument does — a bare string and the object form ({article: '123'}) — and holds the value to
     * what an id can look like: the widget page loads the article by this key, so anything else is a typo
     * on the host page rather than a request worth routing. A slug may carry the language it is meant in
     * ("de/so-gehts"), which is what a help link reads out of its own address (article-links.js): slugs
     * are unique per language only.
     * @param {unknown} arg - Second argument of LeadWin('article', …)
     * @returns {string|null}
     */
    function readArticleId(arg) {
        const raw = typeof arg === 'string'
            ? arg
            : arg && typeof arg.article === 'string' ? arg.article : '';
        const articleId = raw.trim();

        return articleId.length > 0
               && articleId.length <= maxArticleKeyLength
               && /^(?:[a-z]{2}\/)?[\w-]+$/.test(articleId)
            ? articleId
            : null;
    }

    /**
     * Hands one Help-Center article to the mounted circuit. Only an id travels; the page loads the article
     * itself and refuses one outside this widget's own knowledge base. A help link's request carries the
     * id the frame answers it under (see article-link-fallback.js).
     * @param {string} articleId - Validated article id
     * @param {number|null} [requestId] - The help link's request id; none for a JS call
     */
    function postArticleToFrame(articleId, requestId) {
        const message = { type: 'feedbax:widget:showarticle', key: articleId };

        if (requestId) {
            message.requestId = requestId;
        }

        postToFrame(message);
    }

    /**
     * Takes a help link's request back: the visitor has been sent to the link's own page, and its article
     * must not open over it where that page does not reload.
     * @param {number} requestId - The request given up on
     */
    function forgetArticleRequest(requestId) {
        postToFrame({ type: 'feedbax:widget:forgetarticle', requestId: requestId });
    }

    function postToFrame(message) {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage(message, origin);
        } catch { /* frame not loaded yet — the pending route covers it */ }
    }

    /**
     * LeadWin('article', …): opens the panel on one Help-Center article. Deliberately a message
     * rather than a frame URL parameter, for the reason openTrustCard states: a URL keeps its deep link for
     * the life of the frame, so a later reload of it would reopen an article the visitor had just closed.
     * The article panel asks for the reading size itself once it is up (feedbax:widget:expand), which is
     * why nothing here resizes anything.
     * @param {unknown} arg - Article id, bare or as {article: '…'}
     * @param {number} [requestId] - A help link's request id, which the frame's answer carries
     * @returns {boolean} Whether the panel took the article; a declarative link keeps its own navigation
     *                    when it did not
     */
    function openArticle(arg, requestId) {
        const articleId = readArticleId(arg);

        ctx.forgetArticleLink?.(); // A help link is waiting on the article asked for last, if on any.

        if (!articleId) {
            return false; // Nothing to open — an unnamed article would land the visitor on a blank panel.
        }

        if (state.iframe && state.frameSubscribed) {
            postArticleToFrame(articleId, requestId);
        } else {
            state.pendingArticleId = articleId; // Replayed on the circuit's subscription handshake.
            state.pendingArticleRequestId = requestId || null;
        }

        if (!state.panelOpen) {
            setOpen(true);
        }

        return true;
    }

    /**
     * The validated ask command, or null. Accepts the same two shapes every argument here does — a bare
     * string and the object form ({prompt: '…'}) — and holds the text to what a visitor could have typed
     * themselves: it becomes the visitor's own message, so a host page sending a novel would spend the
     * conversation on it.
     *
     * The object form carries one more thing: newChat, whether the question starts a conversation of its
     * own instead of joining the one the visitor has open. Default is joining, because a button sits beside
     * what it asks about and the visitor pressing it is usually mid-thread — the thread that gives the
     * answer its context. A page that asks something unrelated to whatever came before says so.
     * @param {unknown} arg - Second argument of LeadWin('ask', …)
     * @returns {{prompt: string, newChat: boolean}|null}
     */
    function readPrompt(arg) {
        const raw = typeof arg === 'string'
            ? arg
            : arg && typeof arg.prompt === 'string' ? arg.prompt : '';
        const prompt = raw.trim();

        if (prompt.length === 0 || prompt.length > 1000) {
            return null;
        }

        return { prompt: prompt, newChat: Boolean(arg) && arg.newChat === true };
    }

    /**
     * Hands one question to the mounted circuit, which sends it as the visitor's own message.
     * @param {{prompt: string, newChat: boolean}} ask - Validated ask command
     */
    function postPromptToFrame(ask) {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage(
                { type: 'feedbax:widget:ask', prompt: ask.prompt, newChat: ask.newChat === true }, origin);
        } catch { /* frame not loaded yet — the pending route covers it */ }
    }

    /**
     * LeadWin('ask', …): opens the chat and asks one question in the visitor's name — the host
     * page's own button standing in for a sentence the visitor would otherwise have to type ("I need my
     * badge as a PNG file"). A message rather than a frame URL parameter for the reason openArticle
     * states: a deep link in the URL would ask the question again on every reload of the frame.
     * @param {unknown} arg - Prompt text, bare or as {prompt: '…', newChat: false}
     */
    function askPrompt(arg) {
        const ask = readPrompt(arg);

        if (!ask) {
            return; // Nothing to ask — an empty prompt would open a chat that then says nothing.
        }

        forgetPendingArticle(); // The question leads to the chat, and an article replayed after it would cover it.

        if (state.iframe && state.frameSubscribed) {
            postPromptToFrame(ask);
        } else {
            state.pendingPrompt = ask; // Replayed on the circuit's subscription handshake.
        }

        if (!state.panelOpen) {
            setOpen(true);
        }
    }

    /**
     * The look the corner should wear right now. The API scheme is the contractor handing the answer to
     * the host page's own call — light until it arrives, because a page that has not spoken yet is far
     * likelier served light than dark. Every other scheme keeps its own answer: System the visitor's OS
     * ('auto', resolved by isDarkTheme/the stylesheet), "Hell"/"Dunkel" the contractor's forced look.
     * The gate matters beyond taste: our own app pushes its light mode down on every page load, so a
     * scheme that listened to the call uninvited kept overruling a saved dark, reload after reload.
     * One place where the call speaks for every scheme: a stage (isStage). That page is ours, standing in
     * for the provider's website, and its own dark-mode switch is the visitor's choice of look for the
     * whole page, the agent included. A stage that never calls keeps the widget's own answer.
     */
    function effectiveTheme() {
        if (options.theme === 'api') {
            return state.hostTheme || 'light';
        }

        if (isStage && state.hostTheme) {
            return state.hostTheme;
        }

        return options.theme;
    }

    /** Whether a host override is both there and allowed to speak — see effectiveTheme. */
    function hostOverrideApplies() {
        return Boolean(state.hostTheme) && (options.theme === 'api' || isStage);
    }

    /**
     * Repaints the launcher for the look it is standing in. The bubbles switch through a data attribute
     * and their stylesheet; the button carries inline styles and has to be told.
     */
    function paintLauncher() {
        const colors = launcherColors(options, effectiveTheme());
        button.style.backgroundColor = colors.background;
        button.style.backgroundImage = colors.gradient;
        button.style.color = colors.color;
        paintLauncherPulse(button, colors);
    }

    /** Repaints the panel's outline and ground for the look it is standing in — see panelChrome. */
    function paintPanel() {
        paintPanelGround(panel, panelChrome(effectiveTheme(), options.tint));
    }

    /**
     * The visitor crossed the breakpoint mid-visit: a phone turned to landscape, a tablet's split view
     * resized, a desktop window dragged narrow. The panel is rebuilt for the room it is in now — corner
     * box or whole screen — without an animation, because nothing about this is a movement the visitor
     * asked for; they turned the device and expect to find the chat already fitted to it.
     * The takeover follows: a panel that just became fullscreen has to put the launcher away, and one
     * that just stopped being fullscreen has to give the page its scrolling back.
     */
    function handleViewportChange() {
        panel.style.transition = 'none';
        applyPanelGeometry(panel, options, state.panelExpanded === true);
        paintPanel();
        applyFullscreenTakeover(state.panelOpen === true);
    }

    try {
        window.matchMedia(mobileViewportQuery).addEventListener('change', handleViewportChange);
    } catch { /* no matchMedia (or no addEventListener on it): the box stays what the first paint made it */ }

    function postThemeToFrame() {
        if (!state.iframe || !state.iframe.contentWindow || !state.frameDocumentLive) {
            return; // See frame.postPanelState: the mount carries the theme in its URL, the handshake replays it.
        }

        try {
            // The widget's own setting when the override was cleared — handing 'auto' back would put a
            // forced light widget on the visitor's OS preference instead of the look it is configured in.
            state.iframe.contentWindow.postMessage(
                { type: 'feedbax:widget:theme', theme: effectiveTheme() }, origin);
        } catch { /* frame not loaded yet — it mounts with the theme in its URL */ }
    }

    /**
     * LeadWin('theme', …): the host page reports its own dark-mode switch. It speaks exactly
     * where the widget's scheme is API — the contractor handing the look to this call — and stays
     * silent on the other three, which already carry their own answer (see effectiveTheme), except on a
     * stage, where the page's switch speaks for every scheme. The value
     * is stored either way, like the language command's: a scheme saved over to API mid-visit picks up
     * the call the page already made. 'auto' clears the override (back to light, until the next call);
     * nothing here is persisted.
     * @param {unknown} value - 'dark' | 'light' | 'auto' ('auto' clears the override)
     */
    function applyHostTheme(value) {
        const theme = typeof value === 'string' ? value.trim().toLowerCase() : '';
        if (theme !== 'dark' && theme !== 'light' && theme !== 'auto') {
            return;
        }

        state.hostTheme = theme === 'auto' ? null : theme;
        repaintForTheme();
        postThemeToFrame();
    }

    /**
     * Repaints everything the LOADER owns for the look it is standing in right now. Two callers change
     * what that look is — the host page's theme call and the contractor's live save — and both leave
     * the same set of elements behind: the ones carrying their own data-theme for the stylesheet, plus
     * launcher and panel, which wear inline colors and have to be told.
     */
    function repaintForTheme() {
        const theme = effectiveTheme();

        if (state.nudgeStack) {
            state.nudgeStack.dataset.theme = theme;
        }
        ctx.inbox?.applyTheme(theme);
        if (state.trustCardsEl) {
            state.trustCardsEl.dataset.theme = theme;
        }
        // The wrapper carries it too: the close control and the opt-out chip that outlives the card
        // take their colors from tokens defined on the corner, not on what stood inside it.
        if (state.trustCardsCorner) {
            state.trustCardsCorner.dataset.theme = theme;
        }
        if (state.announcementBackdrop) {
            state.announcementBackdrop.dataset.theme = theme;
        }

        paintLauncher();
        paintPanel();
    }

    /**
     * LeadWin('language', …): the host page's own language switcher carries the widget along
     * without a page load — on classic sites the switch navigates and the loader re-reads the page's
     * declaration anyway, so this call is for the SPAs where it does not. Accepts full culture names
     * ('de-DE'); only the widget's own content languages apply, 'auto' hands the decision back to the
     * page's declaration (data-lang, then html lang). Like the theme command it speaks for one visitor
     * on one page and never changes the widget's stored setting. A mounted chat remounts in the new
     * language; the server resumes the running conversation. The bubbles already on screen keep the
     * language they were fetched in — they fall with the next navigation, which SPAs perform on every
     * route change. Silent for a language the widget does not serve: effectiveLanguage would not move,
     * so there is nothing to remount — the same gate the theme command has, and the server applies it
     * too, which is what makes this a spared mount rather than a second rule.
     * @param {unknown} value - a content language ('de' | 'en' | 'fr' | 'nl' | 'es') | 'auto' ('auto' clears the override)
     */
    function applyHostLanguage(value) {
        const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
        if (!raw) {
            return;
        }

        const language = raw === 'auto' ? null : raw.slice(0, 2);
        if (language !== null && runtime.contentLanguages.indexOf(language) < 0) {
            return; // Unknown or unserved language — dropped like every other unknown API input.
        }

        const previous = effectiveLanguage();
        runtime.hostLanguage = language;

        if (effectiveLanguage() === previous) {
            return; // Same language as before — nothing to remount.
        }

        if (state.iframe) {
            frame.mountFrame(); // The fresh URL carries the language; an unmounted widget simply mounts with it.
        }
    }

    return {
        setOpen,
        setExpanded,
        readScreen,
        postScreenToFrame,
        requestScreen,
        openScreen,
        postArticleToFrame,
        openArticle,
        forgetArticleRequest,
        forgetPendingArticle,
        postPromptToFrame,
        askPrompt,
        effectiveTheme,
        hostOverrideApplies,
        postThemeToFrame,
        applyHostTheme,
        repaintForTheme,
        applyHostLanguage
    };
}
